/**
 * Traducción al español (es-ES).
 * Debe implementar EXACTAMENTE la misma forma que `pt.ts` (garantizado por el tipo `Translations`).
 */
import type { Translations } from '..';

const es: Translations = {
  meta: {
    htmlLang: 'es-ES',
  },
  common: {
    loading: 'Cargando...',
    close: 'Cerrar',
    cancel: 'Cancelar',
    ok: 'OK',
    continue: 'Continuar',
    back: 'Volver',
    error: 'Error',
    attention: 'Atención',
    success: 'Éxito',
    oops: 'Vaya...',
    copied: '¡Copiado!',
  },
  layout: {
    loading: 'Cargando...',
    errorTitle: 'Error al cargar la configuración',
    errorBody: 'No hemos podido cargar la configuración de esta página. Inténtalo de nuevo en unos instantes.',
  },
  languageSwitcher: {
    label: 'Idioma',
    pt: 'Português',
    en: 'English',
    es: 'Español',
  },
  /** Inicio — paso 1: indicar la línea. */
  home: {
    title: 'Recarga Fácil',
    subtitle: 'Indica el número que quieres recargar',
    phoneLabel: 'Número de móvil',
    phonePlaceholder: '(00) 00000-0000',
    submit: 'Confirmar número',
    errors: {
      phoneRequired: 'Por favor, introduce tu teléfono',
      phoneLength: 'El teléfono debe tener el prefijo + 9 dígitos',
      lineNotFound: 'Línea no encontrada.',
      lineCheckFailed: 'Error al validar la línea. Inténtalo de nuevo.',
    },
  },
  /** Inicio — paso 2: validación del código SMS. */
  otp: {
    title: 'Seguridad',
    /** {{phone}} se sustituye por el número con formato. */
    subtitle: 'Hemos enviado un código SMS al {{phone}}',
    codeLabel: 'Código SMS',
    codePlaceholder: '000000',
    submit: 'Validar acceso',
    /** {{seconds}} es el tiempo restante del margen de reenvío. */
    resendCountdown: 'Reenviar código ({{seconds}}s)',
    resend: 'Reenviar código',
    resent: 'Código reenviado correctamente.',
    errors: {
      length: 'El código debe tener 6 dígitos',
      invalid: 'Código inválido o caducado.',
      validateFailed: 'Error al validar el código',
      resendFailed: 'No se pudo reenviar el SMS.',
      sessionExpired: 'Sesión caducada. Empieza de nuevo.',
      cooldown: 'Espera un momento antes de solicitar un nuevo código para este número.',
    },
  },
  /** Lista de planes y selección de la forma de pago. */
  plans: {
    pageTitle: 'Elige tu plan',
    heading: 'Elige el plan ideal',
    changeNumber: 'Cambiar número',
    customerFallback: 'Cliente',
    planLabel: 'Plan',
    networkBadge: '4G/5G',
    currency: 'R$',
    perMonth: '/mes',
    select: 'Seleccionar plan',
  },
  /** Ventana «¿Cómo quieres pagar?» */
  payment: {
    chooseTitle: '¿Cómo quieres pagar?',
    pix: {
      name: 'PIX',
      hint: 'Aprobación inmediata',
      badge: 'Gana tiempo',
    },
    card: {
      name: 'Tarjeta de crédito',
      hint: 'Paga ahora, directamente en la web',
    },
    boleto: {
      name: 'Boleto bancario',
      hint: 'Puede tardar hasta 3 días',
    },
    processingTitle: 'Procesando...',
    processingBody: 'Espera mientras generamos tu pago',
    errors: {
      generic: 'Error al procesar.',
      timeout: 'La solicitud ha tardado demasiado. Comprueba tu conexión.',
      noPix: 'No se han recibido los datos del PIX. Inténtalo de nuevo.',
      noBoleto: 'La API no ha devuelto los datos del boleto.',
    },
  },
  /** Ventana del PIX + consulta de confirmación. */
  pix: {
    title: 'Pago por PIX',
    instructions: 'Escanea el código QR o usa el código de copiar y pegar.',
    copyCode: 'Copiar código Pix',
    qrAlt: 'Código QR del PIX',
    confirmedTitle: '¡Pago confirmado!',
    confirmedBody: 'Tu recarga se ha realizado correctamente.',
    timeoutTitle: 'Tiempo agotado',
    timeoutBody:
      'No hemos recibido la confirmación del pago. Si ya has hecho el PIX, espera unos minutos. Si no, genera un código nuevo.',
    waiting: 'Esperando la confirmación del pago...',
  },
  /** Ventana del boleto. */
  boleto: {
    title: 'Boleto bancario',
    instructions: 'Utiliza el código de barras o abre el enlace.',
    copyBarcode: 'Copiar código de barras',
    open: 'Abrir boleto (PDF)',
  },
  /** Pantalla de pago con tarjeta de crédito. */
  creditCard: {
    pageTitle: 'Tarjeta de crédito',
    summaryTitle: 'Resumen del pedido',
    summarySubtitle: 'Comprueba los datos de tu recarga.',
    selectedPlan: 'Plan seleccionado',
    amount: 'Importe',
    line: 'Línea',
    document: 'Documento',
    formTitle: 'Datos del pago',
    nameLabel: 'Nombre en la tarjeta',
    namePlaceholder: 'Tal como aparece impreso en la tarjeta',
    numberLabel: 'Número de la tarjeta',
    numberPlaceholder: '0000 0000 0000 0000',
    expiryLabel: 'Caducidad',
    expiryPlaceholder: 'MM/AAAA',
    cvvLabel: 'CVV',
    cvvPlaceholder: '123',
    /** Etiqueta accesible del icono de marca que se muestra dentro del campo del número. */
    brandIconAlt: 'Marca de la tarjeta',
    recurrenceLabel: 'Activar la recurrencia en la tarjeta',
    recurrenceHint: 'El pago se renovará automáticamente cada mes.',
    submit: 'Realizar pago',
    submitting: 'Procesando...',
    secureFooter: 'Entorno 100 % seguro. Tus datos están protegidos.',
    approvedTitle: '¡Pago aprobado!',
    approvedBody: 'Tu recarga se ha realizado correctamente.',
    backHome: 'Volver al inicio',
    errors: {
      generic: 'Error al procesar el pago.',
      failed: 'Fallo en el procesamiento',
      invalidName: 'Indica el nombre tal como aparece impreso en la tarjeta.',
      invalidNumber: 'Número de tarjeta inválido.',
      invalidExpiry: 'Caducidad inválida. Usa el formato MM/AAAA.',
      expiredCard: 'Tarjeta caducada.',
      invalidCvv: 'CVV inválido.',
      recaptchaFailed: 'No pudimos completar la verificación de seguridad. Recarga la página e inténtalo de nuevo.',
      recaptchaUnavailable: 'El pago con tarjeta no está disponible temporalmente. Usa PIX o boleto.',
    },
  },
  /**
   * Flujo A (heredado) — página `/renova_plano?t={tipo}&id={id}`, consumida por el
   * producto Link de Venda Direta. Los textos son idénticos a los de la vista Blade.
   */
  legacyPayment: {
    /** {{name}} es el nombre de pila del cliente. */
    greeting: 'Hola, {{name}}',
    /** {{product}} procede de `product.*` más abajo. */
    subtitle: 'Realiza el pago de tu {{product}}',
    product: {
      recarga: 'recarga',
      ativacao: 'activación',
      compra: 'compra',
    },
    pixDelay: 'El pago puede tardar hasta 15 minutos en compensarse.',
    pixCopyIntroHighlight: 'Copia y pega',
    pixCopyIntro: 'el código de abajo en la app de tu banco, dentro de la opción PIX.',
    pixCopyButton: 'Copiar código',
    pixScanHighlight: 'Escanea el QRCode',
    pixScanIntro: 'de abajo después de entrar en Pix, en la app de tu banco',
    pixQrAlt: 'Tu código QR',
    boletoDelay: 'El pago puede tardar hasta 2 días hábiles en compensarse.',
    boletoView: 'VER BOLETO',
    boletoCopy: 'COPIAR CÓDIGO',
    linkIntro: 'Elige si quieres pagar con pix, tarjeta de crédito o boleto.',
    linkButton: 'ELEGIR FORMA DE PAGO',
    supportPrefix: '¿Dudas? Habla con nuestro',
    supportLink: 'soporte',
    rightsReserved: 'Todos los derechos reservados',
    notFound: 'No se ha podido localizar el pago.',
  },
  /** Pie de página compartido. */
  footer: {
    customerService: 'Centro de atención',
    whatsapp: 'WhatsApp',
    androidApp: 'Descarga la app en Android',
    googlePlay: 'Google Play',
    iosApp: 'Descarga la app en iPhone',
    appStore: 'App Store',
    website: 'Visita nuestra web',
    /** {{year}} es el año en curso. */
    copyright:
      '© {{year}} Nogle Tech. Todos los derechos reservados. Prohibida la reproducción sin autorización. Este es un entorno seguro. Todos los datos se cifran para proteger tu privacidad.',
    nogleAlt: 'Marca Nogle',
  },
  /** Página de marca inactiva / ruta inexistente (equivalente a service_unavailable). */
  notFound: {
    pageTitle: 'Página no encontrada',
    code: '404',
    title: 'Página no encontrada',
    body: 'Lo sentimos, no hemos podido encontrar la página que buscas.',
    /** {{year}} es el año en curso. */
    copyright: '© {{year}} Recarga Fácil. Todos los derechos reservados.',
  },
  /** Mensajes de error de la capa de API. */
  api: {
    sessionExpired: 'Tu sesión ha caducado. Indica el número de nuevo.',
  },
};

export default es;
